import { createConnectorSoundOrdering, createPivotalGrouping, createSequentialGrouping, createSoundGrouping } from "./grouper_functions"
import { ASTNodeTypes as A, SourceNodeTypes as S } from "../types";
import { NodeGenerator } from ".";
import { ASTNodeDescriptor, GroupedNodeList } from "../../../structures/nodes/ast-node/descriptor"
import { SourceNode } from "../../../structures/nodes/source-node"

export type NodeBlueprint = {
    // anchor nodes must be one level below the top level of the parse tree
    anchors: Set<string>, 
    plans: {
        [nodeName: string]: {
            sourceNodeTypes: S[]
            classList?: string[]
            group?(syntaxNodes: GroupedNodeList, generator: NodeGenerator): ASTNodeDescriptor[]
        }
    }
}

export const default_blueprint = {
    // All nodes are positioned relative to the anchor node within which they are positioned.
    top: S.Top,
    anchors: new Set([S.TabSegment, S.Comment]), 
    plans: {
        Comment: { sourceNodeTypes: [S.Comment] },
        TabSegment: {
            sourceNodeTypes: [S.Modifier, S.TabSegmentLine],
            group(syntaxNodes, generator) {
                const result = [];
                result.push(
                    ...syntaxNodes[S.Modifier].map(node => generator.generateNode(node))
                    .filter(node => !!node)
                )

                const stringsByLine = syntaxNodes[S.TabSegmentLine].map(segmentLine => segmentLine.getChildren(S.TabString))
                result.push(
                    ...createPivotalGrouping(stringsByLine, generator.sourceText)
                    .map(group => generator.constructNode(A.TabBlock, {
                        [S.TabString]: group
                    }))
                    .filter(node => !!node)
                )
                return result;
            }
        },
        TabBlock: {
            sourceNodeTypes: [S.TabString],
            group(syntaxNodes, generator) {
                const result = []

                // first children are Multipliers
                syntaxNodes[S.TabString].forEach(string => {
                    result.push(...string.getChildren(S.Multiplier).map(node => generator.generateNode(node)))
                });

                // next is LineNaming
                const linenames:SourceNode[] = [];
                syntaxNodes[S.TabString].forEach(string => {
                    const name = string.getChild(S.MeasureLineName);
                    name && linenames.push(name)
                })
                result.push(generator.constructNode(A.LineNaming, { [S.MeasureLineName]: linenames }));

                // next are Measures
                const measurelinesByLine:SourceNode[][] = [];
                syntaxNodes[S.TabString].forEach(string => {
                    measurelinesByLine.push(string.getChildren(S.MeasureLine));
                })
                result.push(
                    ...createSequentialGrouping(measurelinesByLine)
                    .map(group => generator.constructNode(A.Measure, {
                        [S.MeasureLine]: group
                    }))
                )
                return result;
            }
        },
        LineNaming: {
            sourceNodeTypes: [S.MeasureLineName],
            group(syntaxNodes, generator) {
                return syntaxNodes[S.MeasureLineName].map(node => generator.generateNode(node))
            }
        },
        Measure: {
            sourceNodeTypes: [S.MeasureLine],
            group(syntaxNodes, generator) {
                const componentsByLine: SourceNode[][] = [];
                const connectorsByLine: SourceNode[][] = [];
                const measurelineStartIndices: number[] = [];
                syntaxNodes[S.MeasureLine].forEach(node => {
                    measurelineStartIndices.push(node.fixed.from);
                    componentsByLine.push(node.getChildren(S.Component));
                    connectorsByLine.push(node.getChildren(S.Connector));
                });

                const sounds = createSoundGrouping(componentsByLine, measurelineStartIndices, generator.sourceText);
                const connectorSoundOrdering = createConnectorSoundOrdering(connectorsByLine, sounds, generator.sourceText);

                const result = connectorSoundOrdering.map(grouping => {
                    if (grouping.type==A.ConnectorGroup) {
                        return generator.constructNode(A.ConnectorGroup, {
                            [S.Connector]: grouping.group
                        })
                    } else {
                        return generator.constructNode(A.Sound, {
                            [S.Component]: grouping.group
                        })
                    }
                })
                return result;
            }
        },
        ConnectorGroup: {
            sourceNodeTypes: [S.Connector],
            group(syntaxNodes, generator) {
                return syntaxNodes[S.Connector].map(node => generator.generateNode(node))
            }
        },
        Sound: {
            sourceNodeTypes: [S.Component],
            group(syntaxNodes, generator) {
                return syntaxNodes[S.Component].map(node => generator.generateNode(node))
            }
        },
        Multiplier: { sourceNodeTypes: [S.Multiplier] },
        MeasureLineName: { sourceNodeTypes: [S.MeasureLineName] },
        Connector: { sourceNodeTypes: [S.Connector] },
        Component: { sourceNodeTypes: [S.Component] }
    }
} as NodeBlueprint